import { memo, useEffect, useMemo, useState } from 'react';

import { DateTime, Text } from '@/components/core';
import { useAgentRunTree } from '@/hooks';
import { toAgentRunId, useGetAgentRun } from '@/hooks/api';

export type ElapsedProps = {
  agentId: string;
  runId: string;
};

const IElapsed = ({ agentId, runId }: ElapsedProps) => {
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));
  const { predictionEvents } = useAgentRunTree({ agentId, runId });

  const [isPredicting, startDate] = useMemo(() => {
    if (predictionEvents.length > 0) {
      const event = predictionEvents[predictionEvents.length - 1];
      return [
        run?.status === 'running' && event.status === 'pending',
        predictionEvents.length === 1 ? run?.createdTime : event.timestamp,
      ];
    }
    return [true, run?.createdTime];
  }, [predictionEvents, run]);

  useEffect(() => {
    if (!isPredicting || !run) return;

    let frameId: number;
    let lastUpdate = Date.now();

    const updateTime = () => {
      const now = Date.now();

      if (now - lastUpdate >= 100) {
        setCurrentTime(now);
        lastUpdate = now;
      }

      frameId = requestAnimationFrame(updateTime);
    };

    frameId = requestAnimationFrame(updateTime);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [isPredicting, run]);

  if (!startDate) return null;

  const elapsedMs = currentTime - startDate.getTime();
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((elapsedMs % 1000) / 100);

  if (!isPredicting || elapsedMs >= 60 * 1000 || elapsedMs < 0) return <DateTime date={run?.createdTime} />;

  return (
    <Text>
      {seconds.toString()}.{tenths}s ago
    </Text>
  );
};

export const Elapsed = memo(IElapsed) as typeof IElapsed;
